import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { XCircle, Search, MessageSquare, ChevronLeft, ChevronRight, } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell
} from '../components/ui/Table';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { customerAPI } from '../services/ApiServices';
import { ExportOptions } from '../components/common/ExportOptions';
import { useExport } from '../hooks/useExport';

export const Rejected = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [exportCustomers, setExportCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isExporting, setIsExporting] = useState(false);
  const [pagination, setPagination] = useState(null);
  const limit = 10;

  const { handleExport } = useExport(
    'rejected_customers',
    'Rejected Customers Report'
  );

  useEffect(() => {
    const delay = setTimeout(() => {
      fetchRejected();
      fetchAllRejectedCustomers();
    }, 300); // debounce

    return () => clearTimeout(delay);
  }, [currentPage, searchTerm]);

  const fetchRejected = async () => {
    try {
      const { data, pagination } = await customerAPI.getRejectedCustomers(
        {
          page: currentPage,
          limit,
          search: searchTerm
        });

      setCustomers(data);
      setFilteredCustomers(data);
      setPagination(pagination);
    } catch (error) {
      console.error('Failed to fetch rejected customers:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllRejectedCustomers = async () => {
    setIsExporting(true);
    try {

      const { data } = await customerAPI.getRejectedCustomers({
        searchTerm,
      });
      setExportCustomers(data);
      // const { data } = await customerAPI.getRejectedCustomers({ search: searchTerm });
      return data;
    } catch (error) {
      console.error('Failed to fetch all rejected customers for export:', error);
      throw error;
    } finally {
      setIsExporting(false);
    }
  };

  /* ===============================
     Fetch Rejected Customers
  =============================== */
  // useEffect(() => {
  //   const fetchRejected = async () => {
  //     try {
  //       const response = await customerAPI.getRejectedCustomers();

  //       setCustomers(response.data);
  //       setFilteredCustomers(response.data);
  //     } catch (error) {
  //       console.error('Failed to fetch rejected customers:', error);
  //     } finally {
  //       setIsLoading(false);
  //     }
  //   };

  //   fetchRejected();
  // }, []);

  /* ===============================
     Search Filter
  =============================== */
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredCustomers(customers);
      return;
    }

    const term = searchTerm.toLowerCase();

    const filtered = customers.filter(
      (item) =>
        item.ofsData?.fullName?.toLowerCase().includes(term) ||
        item.ofsData?.customerId?.toLowerCase().includes(term) ||
        item.referenceNumber?.toLowerCase().includes(term)
    );

    setFilteredCustomers(filtered);
  }, [searchTerm, customers]);

  /* ===============================
     Utils
  =============================== */
  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

  /* ===============================
     Export Config
  =============================== */
  const exportColumns = [
    {
      header: 'Sub ID',
      accessor: (item) => `'${item.faydaData?.sub || ''}`,
      width: 20,
      pdfWidth: 30,
    },
    {
      header: 'Customer ID',
      accessor: (row) => row.ofsData?.customerId
    },
    {
      header: 'Name',
      accessor: (row) => row.ofsData?.fullName
    },
    {
      header: 'Reference No',
      accessor: (row) => row.referenceNumber
    },
    {
      header: 'Company Code',
      accessor: (row) => row.ofsData?.companyCode
    },
    {
      header: 'Status',
      accessor: (row) => row.status
    },
    {
      header: 'Rejected By',
      accessor: (row) =>
        row.reviewedBy
          ? `${row.reviewedBy.firstName} ${row.reviewedBy.lastName}`
          : '-'
    },
    {
      header: 'Rejected At',
      accessor: (row) => formatDate(row.reviewedAt)
    },
    {
      header: 'Reason',
      accessor: (row) => row.rejectionReason
    }
  ];


  const onExport = async (format) => {
    // handleExport(format, customers, exportColumns);
    try {
      // Fetch all pending customers for export
      const allCustomers = await fetchAllRejectedCustomers();

      // Use your export hook with all data
      // Assuming your useExport hook can be called directly:
      handleExport(format, allCustomers, exportColumns);
    } catch (error) {
      console.error('Export failed:', error);
      // You might want to show an error toast here
    }
  };

  /* ===============================
     Loading
  =============================== */
  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        {/* ================= HEADER ================= */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <XCircle className="w-8 h-8 text-red-600" />
              <h1 className="text-3xl font-bold text-gray-900">Rejected</h1>
            </div>
            <p className="text-gray-600">
              Customers with rejected verification
            </p>
          </div>

          <div className="flex items-center gap-4">
            <div className="bg-red-50 px-4 py-2 rounded-lg border border-red-200">
              <p className="text-sm font-semibold text-red-800">
                {pagination.total} Rejected
              </p>
            </div>

            <ExportOptions
              data={exportCustomers}
              onExport={onExport}
              isLoading={isExporting}
              disabled={isLoading}
            />
          </div>
        </div>

        {/* ================= SEARCH ================= */}
        <Card padding="none">
          <div className="p-6 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                placeholder="Search by name, customer ID, or reference number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-full px-4 py-3"
              />
            </div>
          </div>

          {/* ================= TABLE ================= */}
          {filteredCustomers.length > 0 ? (
            <>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeader>Sub ID</TableHeader>
                    <TableHeader>Customer ID</TableHeader>
                    <TableHeader>Name</TableHeader>
                    <TableHeader>Reference No</TableHeader>
                    <TableHeader>Status</TableHeader>
                    <TableHeader>Rejected By</TableHeader>
                    <TableHeader>Rejected At</TableHeader>
                    <TableHeader>Reason</TableHeader>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filteredCustomers.map((item) => (
                    <TableRow key={item._id}>
                      <TableCell>
                        <span className="font-mono text-xs font-semibold">
                          {item.faydaData.sub}
                        </span>
                      </TableCell>
                      <TableCell className="font-mono text-xs font-semibold">
                        {item.ofsData?.customerId}
                      </TableCell>

                      <TableCell className="font-medium">
                        {item.ofsData?.fullName}
                      </TableCell>

                      <TableCell className="font-mono text-xs">
                        {item.referenceNumber}
                      </TableCell>

                      <TableCell>
                        <Badge status={item.status} />
                      </TableCell>

                      <TableCell>
                        {item.reviewedBy
                          ? `${item.reviewedBy.firstName} ${item.reviewedBy.lastName}`
                          : '-'}
                      </TableCell>

                      <TableCell>
                        {formatDate(item.reviewedAt)}
                      </TableCell>

                      <TableCell>
                        {item.rejectionReason ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => navigate(`/rejected/:${item.ofsData?.customerId}`, { state: { customer: item } })}
                            className="gap-2"
                          >
                            <MessageSquare className="w-4 h-4" />
                            View
                          </Button>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {pagination && pagination.total > 0 && (
                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">

                  {/* Info */}
                  <div className="text-sm text-gray-600">
                    Showing {(currentPage - 1) * limit + 1} to{' '}
                    {Math.min(currentPage * limit, pagination.total)} of{' '}
                    {pagination.total} results
                  </div>

                  {/* Controls */}
                  <div className="flex items-center gap-2">
                    {/* Previous */}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Previous
                    </Button>

                    {/* Page Numbers */}
                    <div className="flex gap-1">
                      {Array.from({ length: pagination.page }, (_, i) => i + 1).map(page => (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`px-3 py-1 rounded-lg text-sm font-medium
              ${page === currentPage
                              ? 'bg-blue-600 text-white'
                              : 'text-gray-600 hover:bg-gray-100'
                            }`}
                        >
                          {page}
                        </button>
                      ))}
                    </div>

                    {/* Next */}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        setCurrentPage(p => Math.min(pagination.page, p + 1))
                      }
                      disabled={currentPage === pagination.page}
                    >
                      Next
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="p-12 text-center">
              <XCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900">
                No Rejected Customers
              </h3>
            </div>
          )}
        </Card>
      </div>

      {/* ================= MODAL ================= */}
      {selectedCustomer && (
        <Modal
          isOpen
          onClose={() => setSelectedCustomer(null)}
          title="Rejection Reason"
        >
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-gray-900">
                {selectedCustomer.rejectionReason}
              </p>
            </div>

            <div className="text-xs text-gray-500">
              <p>
                Rejected by:{' '}
                <span className="font-semibold">
                  {selectedCustomer.reviewedBy?.firstName}{' '}
                  {selectedCustomer.reviewedBy?.lastName}
                </span>
              </p>
              <p className="mt-1">
                Date:{' '}
                <span className="font-semibold">
                  {formatDate(selectedCustomer.reviewedAt)}
                </span>
              </p>
            </div>
          </div>
        </Modal>
      )}
    </Layout>
  );
};
